import { parseData, arrayToMapById } from "cxa-utils";
import api from "api/flexapi";
import { loadNavigationMenu } from "./commonActions";
import { showToast } from "actions/toastActions";
import { converge, zipObj, pluck } from "ramda";

// Type of action being performed
export const checkoutLoadBegin = "checkoutLoadBegin";
export const checkoutLoadSuccess = "checkoutLoadSuccess";
export const checkoutLoadFailed = "checkoutLoadFailed";
export const checkoutSaveBegin = "checkoutSaveBegin";
export const checkoutSaveSuccess = "checkoutSaveSuccess";
export const checkoutSaveFailed = "checkoutSaveFailed";

export const checkoutRemoveItemBegin = "checkoutRemoveItemBegin";
export const checkoutRemoveItemSuccess = "checkoutRemoveItemSuccess";
export const checkoutRemoveItemFailed = "checkoutRemoveItemFailed";

export const CHECKOUT_PAYMENT_BEGIN = "CHECKOUT_PAYMENT_BEGIN";
export const CHECKOUT_PAYMENT_SUCCESS = "CHECKOUT_PAYMENT_SUCCESS";
export const CHECKOUT_PAYMENT_FAILED = "CHECKOUT_PAYMENT_FAILED";
export const CHECKOUT_PAYMENT_RESET = "CHECKOUT_PAYMENT_RESET";
export const CHECKOUT_PAYMENT_RESET_ERR_MSG = "CHECKOUT_PAYMENT_RESET_ERR_MSG";

export const REMOVE_EXPIRED_ITEMS_REQUEST = "REMOVE_EXPIRED_ITEMS_REQUEST";
export const REMOVE_EXPIRED_ITEMS_SUCCESS = "REMOVE_EXPIRED_ITEMS_SUCCESS";
export const REMOVE_EXPIRED_ITEMS_FAILURE = "REMOVE_EXPIRED_ITEMS_FAILURE";

// Custom checkout
export const checkoutLibertyLoadSuccess = "checkoutLibertyLoadSuccess";

const idValuePairToObject = converge(zipObj, [pluck("id"), pluck("value")]);

const transformCheckoutPanelItemsData = panelData => {
  const { items: rawItems, messages, ...restPanelData } = panelData;
  let messagesArray = [];

  if (messages && messages.message) {
    messagesArray = messagesArray.concat(messages.message);
  }

  if (!rawItems || !rawItems.item) {
    return {
      ...restPanelData,
      items: { byId: {}, all: [] },
      messages: messagesArray,
    };
  }

  const items = [].concat(rawItems.item);
  const { byId: panelItemsById, all: panelItemsAll } = arrayToMapById(items, {
    idKey: "id",
    includeAll: true,
    camelizeKeys: false,
  });
  return {
    ...restPanelData,
    messages: messagesArray,
    items: {
      byId: panelItemsById,
      all: panelItemsAll,
    },
  };
};

const mapItemType = {
  enrolment: "benefits",
};

const transformLibertyCheckoutData = checkoutData => {
  const {
    products: appData,
    cartItems: cartItemsData,
    ...restData
  } = checkoutData;

  // Transform Plans data to return by planId
  const transformPlanData = appData.map(product => {
    const { plans: planData, ...restItem } = product;
    const { byId: planDataById } = arrayToMapById(planData, {
      idKey: "planId",
      includeAll: false,
      camelizeKeys: false,
    });

    return {
      ...restItem,
      plans: { ...planDataById },
    };
  });

  const { byId: productItemById } = arrayToMapById(transformPlanData, {
    idKey: "productId",
    includeAll: false,
    camelizeKeys: false,
  });

  const transformCartItems = cartItemsData.map(obj => {
    const insuredPerson = obj.insuredEntityDetails.filter(obj => obj.visible);

    return {
      ...obj,
      insuredPerson: insuredPerson.reduce(
        (accInsured, currInsured) => currInsured,
        {}
      ),
      insuredEntityDetails: idValuePairToObject(obj.insuredEntityDetails),
    };
  });

  const subTotalAmount = cartItemsData.reduce(
    (accCartItem, currCartItem) => accCartItem + currCartItem.unitTaxAmount,
    0
  );

  return {
    ...restData,
    products: { ...productItemById },
    cartItems: [...transformCartItems],
    totalAmount: subTotalAmount,
  };
};

const transformCheckoutData = checkoutData => {
  const data = parseData(checkoutData, { camelizeKeys: true });
  const {
    accountDeduction: accountDeductionProp,
    shopCheckoutDetails: { data: appData, panel: panelData },
  } = data;

  const { byId: appDataById } = arrayToMapById([].concat(appData), {
    idKey: "id",
    includeAll: false,
    camelizeKeys: true,
  });

  const transformedPanelData = [].concat(panelData).map(item => {
    const { data: itemData, ...restItem } = item;
    const transformedItemData = []
      .concat(itemData)
      .map(transformCheckoutPanelItemsData);

    const { byId: itemDataById } = arrayToMapById(transformedItemData, {
      idKey: "id",
      includeAll: false,
      camelizeKeys: true,
    });
    return {
      ...restItem,
      ...itemDataById,
    };
  });

  const { byId: panelsById } = arrayToMapById(transformedPanelData, {
    idKey: "objectID",
    includeAll: false,
    camelizeKeys: true,
  });
  const accountDeduction = Object.entries(
    accountDeductionProp
  ).reduce((acc, item) => {
    let itemType = item[0]
      .replace("DeductionFrom", "")
      .replace("PurchaseFrom", "");
    itemType = mapItemType[itemType] || itemType;
    acc[itemType] = item[1];
    return acc;
  }, {});

  return {
    accountDeduction,
    data: appDataById,
    panels: {
      ...panelsById,
    },
  };
};

// Call API to load module data
export function load(isCheckoutPage) {
  // Return thunk middleware
  return dispatch => {
    dispatch({ type: checkoutLoadBegin, payload: null });

    api
      .getCheckoutSummary(isCheckoutPage)
      .then(response => {
        dispatch({
          type: checkoutLoadSuccess,
          payload: transformCheckoutData(response.body),
        });
      })
      .catch(err => {
        dispatch({
          type: checkoutLoadFailed,
          payload: err.response ? err.response.body : err,
        });
      });
  };
}

// Call API to load module data
export function save(hasAgreed) {
  // Return thunk middleware
  return dispatch => {
    dispatch({ type: checkoutSaveBegin, payload: null });

    api
      .proceedCheckout(hasAgreed)
      .then(response => {
        dispatch({ type: checkoutSaveSuccess, payload: response.body });
        loadNavigationMenu()(dispatch);
      })
      .catch(err => {
        dispatch({
          type: checkoutSaveFailed,
          payload: err.response ? err.response.body : err,
        });
      });
  };
}


export function removeExpiredItemsFromCart() {
  return dispatch => {
    dispatch({ type: REMOVE_EXPIRED_ITEMS_REQUEST, payload: null });

    api
      .removeExpiredItemsFromShoppingCart()
      .then(response => {
        dispatch({
          type: REMOVE_EXPIRED_ITEMS_SUCCESS,
          payload: response.body,
        });
      })
      .catch(err => {
        dispatch({
          type: REMOVE_EXPIRED_ITEMS_FAILURE,
          payload: err.response ? err.response.body : err,
        });
      });
  };
}


export function reset() {
  return dispatch => {
    dispatch({ type: CHECKOUT_PAYMENT_RESET, payload: null });
  };
}

export function resetErrorMsg() {
  return {
    type: CHECKOUT_PAYMENT_RESET_ERR_MSG,
    payload: null,
  };
}



// WEBPACK FOOTER //
// ./actions/checkoutActions.js